import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiGet, apiPost, ApiError } from './apiClient';
import { loadAssetPrices } from './assetPricesClient';
import type {
    AssetDetail,
    MarketFromAsset,
    MarketsFromAssetResponse,
    AssetsFromMarketResponse,
    EventMarketsResponse,
    OHLCVPoint,
} from './types';

interface HookResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
}

// --- useAssetDetail ---
// Calls GET /api/asset/{assetType}/{ticker}

export function useAssetDetail(
    assetType: string | null,
    ticker: string | null
): HookResult<AssetDetail> {
    const [data, setData] = useState<AssetDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!assetType || !ticker) {
            setData(null);
            setLoading(false);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiGet<AssetDetail>(
            `/api/asset/${assetType}/${ticker}`,
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('asset-detail failed:', err);
                setError(err);
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [assetType, ticker]);

    return { data, loading, error };
}

// --- useAssetDetailWithFallback ---
// Tries common asset types if the exact type is unknown.
// Strategy: try the provided type first, then fallback through common types.

// TODO: "cryptocurrency" is the primary known asset type returned by /api/search-options.
// When search-options returns a valid asset_type, it takes precedence via assetTypeHint
// (see useAssetDetailWithFallback). This fallback list covers direct URL navigations
// where no hint is available. Extend as new asset types become supported by the API.
const ASSET_TYPES = [
    'cryptocurrency',
    'stock',
    'commodity',
    'bond',
    'currency',
];

export function useAssetDetailWithFallback(
    ticker: string | null,
    hintType?: string | null
): HookResult<AssetDetail> & { resolvedType: string | null } {
    const [data, setData] = useState<AssetDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [resolvedType, setResolvedType] = useState<string | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!ticker) {
            setData(null);
            setLoading(false);
            setResolvedType(null);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        const typesToTry = hintType
            ? [hintType, ...ASSET_TYPES.filter(t => t !== hintType)]
            : ASSET_TYPES;

        (async () => {
            for (const t of typesToTry) {
                if (controller.signal.aborted || callId !== callIdRef.current)
                    return;
                try {
                    const res = await apiGet<AssetDetail>(
                        `/api/asset/${t}/${ticker}`,
                        controller.signal
                    );
                    if (
                        controller.signal.aborted ||
                        callId !== callIdRef.current
                    )
                        return;
                    setData(res);
                    setResolvedType(t);
                    setError(null);
                    setLoading(false);
                    return;
                } catch (err) {
                    if (err instanceof ApiError && err.status === 404) {
                        continue;
                    }
                    if (
                        (err as { name?: string })?.name === 'AbortError' ||
                        controller.signal.aborted ||
                        callId !== callIdRef.current
                    ) {
                        return;
                    }
                    console.error(
                        `asset-detail failed for ${t}/${ticker}:`,
                        err
                    );
                    setError(
                        err instanceof Error ? err : new Error(String(err))
                    );
                    setData(null);
                    setLoading(false);
                    return;
                }
            }
            if (controller.signal.aborted || callId !== callIdRef.current)
                return;
            setError(new Error(`Asset "${ticker}" not found in any type`));
            setData(null);
            setLoading(false);
        })();

        return () => controller.abort();
    }, [ticker, hintType]);

    return { data, loading, error, resolvedType };
}

// --- useMarketsFromAsset ---
// Calls POST /api/markets-from-asset

export function useMarketsFromAsset(
    ticker: string | null,
    assetType: string | null,
    options?: { limit?: number }
): HookResult<MarketsFromAssetResponse> {
    const [data, setData] = useState<MarketsFromAssetResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!ticker || !assetType) {
            setData(null);
            setLoading(false);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiPost<MarketsFromAssetResponse>(
            '/api/markets-from-asset',
            {
                ticker,
                asset_type: assetType,
                ...(options?.limit && { limit: options.limit }),
            },
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('markets-from-asset failed:', err);
                setError(err);
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [ticker, assetType, options?.limit]);

    return { data, loading, error };
}

// --- useAssetsFromMarket ---
// Calls POST /api/assets-from-market

export function useAssetsFromMarket(
    conditionId: string | null
): HookResult<AssetsFromMarketResponse> {
    const [data, setData] = useState<AssetsFromMarketResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!conditionId) {
            setData(null);
            setLoading(false);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiPost<AssetsFromMarketResponse>(
            '/api/assets-from-market',
            {
                condition_id: conditionId,
            },
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('assets-from-market failed:', err);
                setError(err);
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [conditionId]);

    return { data, loading, error };
}

// --- useAssetPrices ---
// Loads real OHLCV price data via the loadAssetPrices Firebase callable.
// AbortController cancels in-flight requests when params change.

type TimeRange = '1W' | '1M' | '3M' | '1Y' | 'ALL';

export type CandleFrequency = '1H' | '1D' | '1W' | '1M';

const RANGE_DAYS: Record<TimeRange, number> = {
    '1W': 7,
    '1M': 30,
    '3M': 90,
    '1Y': 365,
    ALL: 365 * 15, // ~15 years covers all crypto/stock history
};

export function useAssetPrices(
    ticker: string | null,
    assetType: string | null,
    timeRange: TimeRange,
    frequency: CandleFrequency = '1D'
): HookResult<OHLCVPoint[]> {
    const [data, setData] = useState<OHLCVPoint[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);

    const { startDate, endDate } = useMemo(() => {
        const end = new Date();
        const start = new Date(
            end.getTime() - RANGE_DAYS[timeRange] * 24 * 60 * 60 * 1000
        );
        return { startDate: start, endDate: end };
    }, [timeRange]);

    useEffect(() => {
        if (!ticker || !assetType) {
            setData(null);
            setLoading(false);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        loadAssetPrices(ticker, assetType, startDate, endDate, {
            signal: controller.signal,
            candleFrequency: frequency,
        })
            .then(points => {
                if (callId !== callIdRef.current) return;
                setData(points);
            })
            .catch(err => {
                if (callId !== callIdRef.current) return;
                if (err?.name === 'AbortError') return;
                console.error('asset-prices failed:', err);
                setError(err);
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, [ticker, assetType, startDate, endDate, frequency]);

    return { data, loading, error };
}

// --- Refetch utility ---

export function useRefetch<T>(
    fetchFn: () => Promise<T>
): HookResult<T> & { refetch: () => void } {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);
    const fetchRef = useRef(fetchFn);
    fetchRef.current = fetchFn;

    const refetch = useCallback(() => {
        const callId = ++callIdRef.current;
        setLoading(true);
        setError(null);

        fetchRef
            .current()
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (callId !== callIdRef.current) return;
                setError(err);
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });
    }, []);

    return { data, loading, error, refetch };
}

// --- useEventMarkets ---
// Calls GET /api/event-markets/{identifier} (accepts event_id or slug)

export function useEventMarkets(
    identifier: string | null
): HookResult<EventMarketsResponse> {
    const [data, setData] = useState<EventMarketsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);

    useEffect(() => {
        if (!identifier) {
            setData(null);
            setLoading(false);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiGet<EventMarketsResponse>(
            `/api/event-markets/${encodeURIComponent(identifier)}`,
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('event-markets failed:', err);
                setError(err);
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => controller.abort();
    }, [identifier]);

    return { data, loading, error };
}
